
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});
//setupApiLogger(apiClient);
apiClient.interceptors.request.use(
  async (config) => {
    const value = await localStorage.getItem('apexcare-token');
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (value) {
      config.headers = {
        Authorization: `Bearer ${value}`,
        'x-user-timezone': timezone,
      };
      if (config) {
        config.headers['Access-Control-Allow-Origin'] = '*';
        config.headers['Access-Control-Allow-Methods'] = 'GET, POST, PUT, DELETE,OPTIONS';
        config.headers['Access-Control-Allow-Headers'] = 'Origin, X-Requested-With, Content-Type, Accept, Authorization';
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Endpoints that legitimately answer 401 to an anonymous caller. A 401 from one
// of these is the API doing its job -- a wrong password, an expired reset token --
// not an expired session. It has to reach the calling component as an ordinary
// error, otherwise a mistyped password bounces the user instead of showing
// "Invalid Password".
const PUBLIC_AUTH_REQUESTS = ['/auth/login', '/auth/forgot-password', '/auth/password-reset'];

// The routes that already are the logged-out experience. Redirecting to the
// login page from the login page would loop.
const isOnLoginSurface = (): boolean => {
  const path = window.location.pathname;
  return path === '/' || path.startsWith('/forget') || path.startsWith('/password-reset');
};

const isPublicAuthRequest = (url?: string): boolean =>
  !!url && PUBLIC_AUTH_REQUESTS.some((publicPath) => url.includes(publicPath));

// useAuth's bootstrap fires roughly 45 calls in parallel. Without this guard an
// expired session would trigger one redirect for every in-flight request.
let sessionExpiryHandled = false;

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;

    if (status === 401 && !sessionExpiryHandled && !isPublicAuthRequest(error?.config?.url) && !isOnLoginSurface()) {
      sessionExpiryHandled = true;

      // The same keys logout() clears. A full navigation rather than a router
      // push, so every in-memory context is rebuilt from scratch: useAuth
      // re-persists its state to localStorage on change, so leaving the app
      // running would let it write the stale user straight back.
      localStorage.removeItem('apexcare-token');
      localStorage.removeItem('user');
      window.location.replace('/');
    }

    return Promise.reject(error);
  },
);

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const retryWithExponentialBackoff = async (fn: () => Promise<any>, retries: number = 0, delayMs: number = 200): Promise<any> => {
  try {
    return await fn();
  } catch (error) {
    if (retries <= 0) throw error;
    await delay(delayMs);
    return retryWithExponentialBackoff(fn, retries - 1, delayMs * 2);
  }
};

const handleApiResponse = (res: AxiosResponse): AxiosResponse => {
  if (res && res.data !== undefined) {
    return res;
  } else {
    throw new Error('Invalid response data');
  }
};

const API = {
  get: async (method: string, config?: AxiosRequestConfig): Promise<any> => retryWithExponentialBackoff(
    () => apiClient.get(method, config).then(handleApiResponse)
  ),
  // put: async (method: string, payload: any): Promise<any> => retryWithExponentialBackoff(
  //   () => apiClient.put(method, payload).then(handleApiResponse)
  // ),
  // post: async (method: string, payload: any): Promise<any> => retryWithExponentialBackoff(
  //   () => apiClient.post(method, payload).then(handleApiResponse)
  // ),
  // delete: async (method: string): Promise<any> => retryWithExponentialBackoff(
  //   () => apiClient.delete(method).then(handleApiResponse)
  // ),
  put(method: string, payload: any, config?: AxiosRequestConfig) {
    return apiClient.put(method, payload, config);
  },
  post(method: string, payload: any, config?: AxiosRequestConfig) {
    return apiClient.post(method, payload, config);
  },
  delete(method: string, config?: AxiosRequestConfig) {
    return apiClient.delete(method, config);
  },
};

export default API;
